import { useState } from 'react';
import { ArrowRight, Building2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { setAuthSession } from '@shared/auth/session';
import { Button, Card, PageHeader } from '../../components/ui';
import { authAPI } from '../../lib/api';
import { PASSWORD_HINT, PASSWORD_MIN } from '../../../components/auth/credentialSchema';

const inputClass = 'w-full rounded-xl px-4 py-3 text-[15px] font-medium';
const inputStyle = { background: 'var(--card)', border: '1px solid var(--border)' } as const;

export default function SignupScreen() {
  const navigate = useNavigate();
  // Organization sign-up links (/join/:code) forward here with ?code= so the
  // membership is created during registration (backend re-validates the code).
  const [searchParams] = useSearchParams();
  const signupCode = searchParams.get('code')?.trim() || undefined;
  // A shared ballot's "Create your own ballot" (/b/:shortId) forwards ?ref= so the
  // new account is credited to that link; absent or stale refs change nothing.
  const shareRef = searchParams.get('ref')?.trim() || undefined;
  // The invite link lands here directly, so name the organization up front.
  // A dead code still lets the person sign up — just without the affiliation.
  const { data: invite } = useQuery(
    ['signup-code', signupCode],
    async () => (await authAPI.signupCode(signupCode as string)).data?.data as { valid?: boolean; organizationName?: string } | undefined,
    { enabled: !!signupCode, retry: false, staleTime: 5 * 60 * 1000 }
  );
  const inviteOrg = invite?.valid ? invite.organizationName : undefined;
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = firstName.trim() && lastName.trim() && /\S+@\S+\.\S+/.test(email) && password.length >= PASSWORD_MIN;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const res = await authAPI.citizenSignup({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        password,
        signupCode,
        shareRef,
      });
      const data = res.data?.data;
      setAuthSession(
        {
          token: data.token,
          user: data.user,
          organization: null,
          membership: null,
          accountType: 'citizen',
        },
        true
      );
      if (data.affiliation?.organizationName) {
        toast.success(`You're connected to ${data.affiliation.organizationName}`);
      } else if (data.affiliationRequested) {
        // Code was invalid/expired by the time the form was submitted — the
        // account was still created, just without the affiliation.
        toast(`That organization invite link is no longer active, so your account isn't connected to it.`);
      }
      navigate('/citizen/intake');
    } catch (err: any) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg ||
        'Could not create your account. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col min-h-screen screen-enter">
      <PageHeader title="Create account" onBack={() => navigate('/citizen')} />

      <form onSubmit={handleSubmit} className="flex-1 flex flex-col px-6 pt-4 pb-6 gap-6">
        {inviteOrg && (
          <Card className="p-4 flex items-center gap-3" style={{ background: 'var(--accent)', border: '1px solid var(--border)' }}>
            <span
              className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'var(--card)', color: 'var(--primary)' }}
              aria-hidden
            >
              <Building2 className="w-5 h-5" />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
                You're joining
              </p>
              <p className="text-[15px] font-extrabold leading-snug">{inviteOrg}</p>
              <p className="text-xs text-[color:var(--muted-foreground)] mt-0.5">
                Your new account will be connected to {inviteOrg}.
              </p>
            </div>
          </Card>
        )}
        {signupCode && invite && !invite.valid && (
          <p className="text-sm rounded-xl px-4 py-3" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
            This invitation link isn't active anymore. You can still create a free account.
          </p>
        )}

        <div className="space-y-2">
          <h2 className="text-2xl font-extrabold tracking-tight">Let's get you set up</h2>
          <p className="text-[15px] text-[color:var(--muted-foreground)]">
            One account for issues, your ballot, and your representatives.
          </p>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
                First name
              </label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
                Last name
              </label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              inputMode="email"
              className={inputClass}
              style={inputStyle}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-[11px] font-bold uppercase tracking-widest text-[color:var(--muted-foreground)]">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className={inputClass}
              style={inputStyle}
            />
            <p className="text-xs text-[color:var(--muted-foreground)]">{PASSWORD_HINT}</p>
          </div>
        </div>

        <Card className="p-4 flex items-center gap-3" style={{ background: 'transparent', border: '1px dashed var(--border)' }}>
          <p className="text-xs text-[color:var(--muted-foreground)] leading-relaxed">
            Your information is encrypted and never shared without explicit permission. <b>Citizen Standard</b>{' '}
            compliant.
          </p>
        </Card>

        <div className="mt-auto space-y-3">
          <Button size="lg" className="w-full" type="submit" disabled={!canSubmit || submitting}>
            {submitting ? 'Creating account…' : (
              <>
                Continue <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
          <button
            type="button"
            // An invitee who already has an account signs in and comes back to
            // /join/:code, which confirms the membership.
            onClick={() =>
              navigate(inviteOrg ? `/citizen/login?returnUrl=${encodeURIComponent(`/join/${signupCode}`)}` : '/citizen/login')
            }
            className="w-full text-center text-sm font-medium text-[color:var(--muted-foreground)] hover-elevate rounded-md py-2"
          >
            Already have an account? Sign in
          </button>
        </div>
      </form>
    </div>
  );
}
